import { ShieldAlert } from 'lucide-react';
import { Badge } from './ui/badge';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import { useTenantUsers } from '@/hooks/useTenantUsers';
import type { AccessChanges } from '@/services/userGroup.service';

type Item = AccessChanges['subjects'][number]['items'][number];

/** The line's text, with any metrics it lists shown as badges. */
function ItemText({ item }: { item: Item }) {
  if (!item.template || !item.metrics?.length) return <>{item.message}</>;
  const [before, after = ''] = item.template.split('{metrics}');
  return (
    <>
      {before}
      <span className="inline-flex flex-wrap gap-1 align-middle">
        {item.metrics.map((name) => (
          <Badge key={name} variant="outline" className="border-amber-500/60 bg-background px-1.5 py-0 text-[11px] font-medium">
            {name}
          </Badge>
        ))}
      </span>
      {after}
    </>
  );
}

/**
 * Data access changes this publish makes (ENG-203), in the publish dialog.
 *
 * Groups' and users' scopes aren't branched, so publishing the model can change
 * what a restricted group sees: a metric that loses its connection to a restricted
 * dimension starts showing all its rows, a new metric is visible unless hidden.
 * When anything widens or breaks, publishing needs an explicit acknowledgement --
 * not a block, because the fix is often to publish and then adjust the group.
 */
export function PublishAccessChanges({
  changes,
  acknowledged,
  onAcknowledgedChange,
}: {
  changes: AccessChanges | undefined;
  acknowledged: boolean;
  onAcknowledgedChange: (acknowledged: boolean) => void;
}) {
  // Users are listed by id; names come from the tenant's user list when the
  // publisher can read it (it's Admin-only), else the id stays.
  const hasUsers = !!changes?.subjects.some((s) => s.kind === 'user');
  const { data: users } = useTenantUsers({ enabled: hasUsers });

  if (!changes?.subjects.length) return null;

  const userName = (id: string) => {
    const u = users?.find((x) => x.id === id);
    return u ? (u.name || u.email) : id;
  };

  return (
    <Alert
      className={
        changes.requires_acknowledgement
          ? 'mt-3 border-amber-500/50 bg-amber-500/10 [&>svg]:text-amber-600'
          : 'mt-3'
      }
    >
      <ShieldAlert className="h-4 w-4" />
      <AlertTitle>This publish changes what restricted users can see</AlertTitle>
      <AlertDescription>
        <p className="mb-2">
          Groups and users with data access restrictions see the published model, so these changes reach them as soon
          as you publish. Adjust them in Security &amp; Governance, before or after publishing.
        </p>
        <ul className="space-y-2 max-h-56 overflow-y-auto">
          {changes.subjects.map((s) => (
            <li key={`${s.kind}-${s.id}`} className="text-xs">
              <span className="font-medium">
                {s.kind === 'group' ? `Group "${s.name}"` : `User ${userName(s.id)}`}
              </span>
              {s.kind === 'group' && s.member_count !== null && (
                <span className="text-muted-foreground"> ({s.member_count} member{s.member_count === 1 ? '' : 's'})</span>
              )}
              <ul className="mt-1 ml-3 list-disc space-y-0.5">
                {s.items.map((item, i) => (
                  <li key={i} className={item.level === 'info' ? 'text-muted-foreground' : undefined}>
                    <ItemText item={item} />
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
        {changes.requires_acknowledgement && (
          <div className="flex items-center gap-2 mt-3">
            <Checkbox
              id="ack-access-changes"
              checked={acknowledged}
              onCheckedChange={(checked) => onAcknowledgedChange(checked === true)}
            />
            <Label htmlFor="ack-access-changes" className="text-xs font-medium">
              I've reviewed these access changes
            </Label>
          </div>
        )}
      </AlertDescription>
    </Alert>
  );
}
